import { Check, Copy, X } from "lucide-react";
import qrcode from "qrcode-generator";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "./../style/TeamShareModal.scss";
import { generateInviteQR } from "../funtion";

interface TeamShareModalProps {
  open: boolean;
  onClose: () => void;
  teamName: string;
  team_id: string;
  joinCode: string;
  showSuccess?: boolean;
}

// Builds the join URL from the current origin: e.g. https://lumir.com/join/<code>
function buildJoinUrl(code: string): string {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/join/${code}`;
}

function buildQrDataUrl(value: string): string {
  const qr = qrcode(0, "M");
  qr.addData(value);
  qr.make();
  return qr.createDataURL(5, 2);
}

const TeamShareModal: React.FC<TeamShareModalProps> = ({
  open,
  onClose,
  teamName,
  team_id,
  joinCode,
  showSuccess = false,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [apiQrUrl, setApiQrUrl] = useState<string | null>(null);

  const joinUrl = useMemo(() => buildJoinUrl(joinCode), [joinCode]);
  const displayUrl = useMemo(() => apiQrUrl || joinUrl, [apiQrUrl, joinUrl]);
  const qrDataUrl = useMemo(() => buildQrDataUrl(displayUrl), [displayUrl]);


  useEffect(() => {
    async function getQRFromApi(id: number): Promise<string | undefined> {
      const data = await generateInviteQR(id);
      if (data?.qr_invite_url) {
        return data.qr_invite_url;
      }
    }
    if (team_id && open && team_id !== "0") {
      getQRFromApi(Number(team_id)).then((url) => {
        if (url) {
          setApiQrUrl(url);
        }
      });
    }
  }, [team_id, open]);

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("FailedToCopy"));
    }
  };



  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9000] p-4">
      <div className="team-share-modal rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
        <div className="team-share-modal__header shrink-0 flex justify-between items-center px-6 py-4 border-b">
          <h3 className="text-lg font-semibold m-0">
            {showSuccess ? t("TeamCreatedSuccess") : t("ShareTeam")}
          </h3>
          <button
            className="team-share-modal__close transition"
            onClick={onClose}
            aria-label={t("Close")}
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-2 flex flex-col items-center overflow-y-auto">
          <p className="w-full text-base font-semibold m-0 mb-1 text-left">{teamName}</p>
          <p className="team-share-modal__desc w-full text-sm text-left m-0 mb-4">
            {t("TeamCreatedShareDesc")}
          </p>

          <div className="team-share-modal__qr-box rounded-xl p-4 flex flex-col items-center mb-4">
            <img src={qrDataUrl} alt={t("ScanToJoin")} className="w-32 h-32" />
            <span className="team-share-modal__qr-label text-xs uppercase tracking-wider mt-2">
              {t("ScanToJoin")}
            </span>
          </div>

          <div className="w-full text-left">
            <label className="block text-sm font-medium mb-1">{t("ShareLink")}</label>
            <input
              readOnly
              value={displayUrl}
              onFocus={(e) => e.currentTarget.select()}
              className="team-share-modal__link-input w-full px-3 py-2.5 rounded-md text-sm mb-2"
            />
            <button
              onClick={handleCopy}
              className={`team-share-modal__copy-btn w-full flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-md text-sm font-medium transition ${copied
                  ? "team-share-modal__copy-btn--copied bg-green-100 text-green-700"
                  : "bg-[#e9b460] text-white hover:opacity-90"
                }`}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? t("Copied") : t("Copy")}
            </button>
          </div>
        </div>

        <div className="team-share-modal__footer shrink-0 px-6 py-4 border-t">
          <button
            onClick={onClose}
            className="team-share-modal__done-btn w-full py-2.5 rounded-md text-sm font-medium transition"
          >
            {t("Done")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TeamShareModal;
